"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/atsa/AppShell";
import { KeyboardCheck } from "@/components/atsa/KeyboardCheck";
import { ModeSelector } from "@/components/atsa/ModeSelector";
import { Results } from "@/components/atsa/Results";
import { TestChrome } from "@/components/atsa/TestChrome";
import { ATSA_PROFILE_2026, type PracticeMode } from "@/lib/atsa/atsaConfig";
import { formatTime, mean, percentile, randomInt } from "@/lib/atsa/utils";

type Phase = "options" | "priming" | "running" | "intermission" | "result";
type Trial = { expected: number; answer: number; correct: boolean; reactionMs: number; round: number };

function nextDigit(previous?: number): number {
  let value = randomInt(1, 9);
  while (previous !== undefined && value === previous) value = randomInt(1, 9);
  return value;
}

export default function MemoryDifferencesPage() {
  const [mode, setMode] = useState<PracticeMode>("training");
  const [phase, setPhase] = useState<Phase>("options");
  const [roundSeconds, setRoundSeconds] = useState(90);
  const [roundCount, setRoundCount] = useState(4);
  const [feedbackEnabled, setFeedbackEnabled] = useState(true);
  const [round, setRound] = useState(1);
  const [remaining, setRemaining] = useState(90);
  const [previous, setPrevious] = useState<number | null>(null);
  const [current, setCurrent] = useState<number | null>(null);
  const [trials, setTrials] = useState<Trial[]>([]);
  const [feedback, setFeedback] = useState<"good" | "bad" | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const stimulusAtRef = useRef(0);

  const effectiveRounds = mode === "simulation" ? ATSA_PROFILE_2026.memory.rounds : roundCount;
  const effectiveSeconds = mode === "simulation" ? ATSA_PROFILE_2026.memory.roundSeconds : roundSeconds;

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const primeRound = useCallback((roundNumber: number) => {
    clearTimeoutRef();
    const first = nextDigit();
    setRound(roundNumber);
    setPrevious(first);
    setCurrent(first);
    setRemaining(effectiveSeconds);
    setPhase("priming");
    timeoutRef.current = window.setTimeout(() => {
      const second = nextDigit(first);
      setCurrent(second);
      stimulusAtRef.current = performance.now();
      setPhase("running");
    }, ATSA_PROFILE_2026.memory.firstDigitMs);
  }, [clearTimeoutRef, effectiveSeconds]);

  const start = useCallback(() => {
    setTrials([]);
    setFeedback(null);
    primeRound(1);
  }, [primeRound]);

  const finishRound = useCallback(() => {
    if (round >= effectiveRounds) {
      setPhase("result");
      return;
    }
    setPhase("intermission");
    timeoutRef.current = window.setTimeout(() => primeRound(round + 1), 2500);
  }, [effectiveRounds, primeRound, round]);

  useEffect(() => {
    if (phase !== "running") return;
    const timer = window.setInterval(() => {
      setRemaining((value) => {
        if (value <= 1) {
          window.clearInterval(timer);
          window.setTimeout(finishRound, 0);
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [finishRound, phase]);

  useEffect(() => {
    if (phase !== "running") return;
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat || previous === null || current === null) return;
      const simulationMatch = /^Numpad[0-8]$/.test(event.code);
      const trainingMatch = simulationMatch || /^[0-8]$/.test(event.key);
      if (mode === "simulation" ? !simulationMatch : !trainingMatch) return;
      event.preventDefault();
      const answer = Number(event.code.startsWith("Numpad") ? event.code.replace("Numpad", "") : event.key);
      const expected = Math.abs(current - previous);
      const correct = answer === expected;
      setTrials((items) => [...items, { expected, answer, correct, reactionMs: performance.now() - stimulusAtRef.current, round }]);
      if (mode === "training" && feedbackEnabled) {
        setFeedback(correct ? "good" : "bad");
        window.setTimeout(() => setFeedback(null), 180);
      }
      const next = nextDigit(current);
      setPrevious(current);
      setCurrent(next);
      stimulusAtRef.current = performance.now();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, feedbackEnabled, mode, phase, previous, round]);

  useEffect(() => () => clearTimeoutRef(), [clearTimeoutRef]);

  const correct = trials.filter((trial) => trial.correct).length;
  const accuracy = trials.length ? Math.round((correct / trials.length) * 100) : 0;
  const reactionTimes = trials.map((trial) => trial.reactionMs);
  const perRound = useMemo(() => Array.from({ length: effectiveRounds }, (_, index) => {
    const items = trials.filter((trial) => trial.round === index + 1);
    return items.length ? Math.round((items.filter((item) => item.correct).length / items.length) * 100) : 0;
  }), [effectiveRounds, trials]);

  const activeSimulation = mode === "simulation" && phase !== "options" && phase !== "result";

  return (
    <AppShell hideChrome={activeSimulation}>
      <TestChrome
        title="Numbers and Differences"
        mode={activeSimulation ? "simulation" : "training"}
        status={phase === "running" || phase === "priming" ? `Round ${round} of ${effectiveRounds} · ${formatTime(remaining)}` : undefined}
        onRestart={mode === "training" && phase !== "options" ? start : undefined}
      >
        {phase === "options" && (
          <section className="panel">
            <div className="panel-header">
              <p className="eyebrow">REPORTED SECTION 1</p>
              <h2>Numbers and Differences</h2>
              <p>Memorize the first digit. Then enter the positive difference between each new digit and the previous displayed digit.</p>
            </div>
            <ModeSelector value={mode} onChange={(next) => {
              setMode(next);
              if (next === "simulation") {
                setFeedbackEnabled(false);
                setRoundSeconds(90);
                setRoundCount(8);
              }
            }} />
            {mode === "training" && (
              <div className="options-grid">
                <div className="profile-block">
                  <span className="profile-label">Session profile</span>
                  <div className="profile-row">
                    {([['Short', 'Warm-up', 4, 90, false], ['Standard', 'Test-like', 8, 90, true], ['Endurance', 'Longer', 8, 120, false]] as const).map(([label, tag, rc, rs, std]) => (
                      <button type="button" key={label} className={`profile-chip ${roundCount === rc && roundSeconds === rs ? "active" : ""}`} onClick={() => { setRoundCount(rc); setRoundSeconds(rs); }}>
                        <span className="tag">{tag}</span>{label}{std ? <span className="std-flag">★ Standard</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="control-group">
                  <label htmlFor="memory-rounds">Rounds</label>
                  <select id="memory-rounds" value={roundCount} onChange={(event) => setRoundCount(Number(event.target.value))}>
                    <option value={1}>1 round</option>
                    <option value={4}>4 rounds</option>
                    <option value={8}>8 rounds</option>
                  </select>
                </div>
                <div className="control-group">
                  <label htmlFor="memory-duration">Seconds per round</label>
                  <select id="memory-duration" value={roundSeconds} onChange={(event) => setRoundSeconds(Number(event.target.value))}>
                    <option value={30}>30 seconds</option>
                    <option value={60}>60 seconds</option>
                    <option value={90}>90 seconds</option>
                    <option value={120}>120 seconds</option>
                  </select>
                </div>
                <div className="control-group">
                  <label>Feedback</label>
                  <div className="check-row">
                    <input id="memory-feedback" type="checkbox" checked={feedbackEnabled} onChange={(event) => setFeedbackEnabled(event.target.checked)} />
                    <label htmlFor="memory-feedback">Brief correctness flash</label>
                  </div>
                </div>
              </div>
            )}
            <ol className="instructions">
              <li>The opening digit displays for two seconds.</li>
              <li>Each response immediately advances the stream.</li>
              <li>Fidelity simulation accepts only physical numpad keys 0–8.</li>
              <li>Eight 90-second rounds are the primary evidence profile. A 120-second profile remains plausible.</li>
            </ol>
            <KeyboardCheck />
            <button className="primary-button" onClick={start}>Start {mode === "simulation" ? "Simulation" : "Training"}</button>
          </section>
        )}

        {(phase === "priming" || phase === "running") && (
          <section className={activeSimulation ? "fidelity-stage number-stage" : "stage"} aria-live="polite">
            <div className="stage-content">
              {mode === "training" && <p className="prompt-label">{phase === "priming" ? "MEMORIZE" : "ENTER THE DIFFERENCE"}</p>}
              <div className="big-number">{current}</div>
              {feedback && <div className={`feedback ${feedback}`}>{feedback === "good" ? "CORRECT" : "WRONG"}</div>}
            </div>
          </section>
        )}

        {phase === "intermission" && (
          <section className={activeSimulation ? "fidelity-stage" : "stage"}>
            <div className="stage-content"><h2>Round {round} complete</h2><p>Next round begins automatically.</p></div>
          </section>
        )}

        {phase === "result" && (
          <Results
            section="Numbers & Differences"
            record={{ sectionSlug: "memory-differences", score: accuracy, scoreLabel: "Accuracy" }}
            title="Numbers and Differences Complete"
            metrics={[
              { label: "Correct", value: correct },
              { label: "Incorrect", value: trials.length - correct },
              { label: "Accuracy", value: `${accuracy}%` },
              { label: "Median response", value: `${Math.round(percentile(reactionTimes, 0.5))} ms` },
              { label: "90th percentile", value: `${Math.round(percentile(reactionTimes, 0.9))} ms` },
              { label: "Average round", value: `${Math.round(mean(perRound))}%` },
            ]}
            onAgain={start}
            onOptions={() => setPhase("options")}
          />
        )}
      </TestChrome>
    </AppShell>
  );
}
